import React, { useState, useEffect } from 'react';
import { Bell, X, MessageCircle } from 'lucide-react';
import { getUnreadAlerts, markAlertAsRead, markAllAlertsAsRead } from '../services/attendanceApi';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';
import api from '../services/api';

function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  // ⭐ NOVO: notificações gerais persistidas (pausa de campanha, opt-out, alerta
  // de setor sem resposta, etc.) — antes só existiam ao vivo via SSE, perdidas
  // se ninguém estivesse com o painel aberto na hora
  const [persistedNotifications, setPersistedNotifications] = useState([]);

  // ⭐ NOVO: notificações em tempo real (mensagens novas via SSE)
  const { notifications: liveNotifications, clearNotifications: clearLive } = useRealtimeNotifications();

  useEffect(() => {
    loadNotifications();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [res, res2] = await Promise.all([
        getUnreadAlerts(),
        api.get('/api/notifications?limit=30')
      ]);
      setNotifications(res.data);
      setPersistedNotifications(res2.data.filter(n => !n.read_at));
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPersistedAsRead = async (id) => {
    try {
      await api.put(`/api/notifications/${id}/read`);
      setPersistedNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Erro ao marcar notificação como lida:', err);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAlertAsRead(id);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await Promise.all([markAllAlertsAsRead(), api.put('/api/notifications/read-all')]);
      setNotifications([]);
      setPersistedNotifications([]);
      clearLive();
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    }
  };

  const unreadCount = notifications.length + liveNotifications.length + persistedNotifications.length;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) loadNotifications();
        }}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
        title="Notificações"
      >
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Overlay para fechar ao clicar fora */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-gray-900">Notificações</h3>
              {(notifications.length > 0 || liveNotifications.length > 0 || persistedNotifications.length > 0) && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-green-600 hover:text-green-700 font-medium"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : notifications.length === 0 && liveNotifications.length === 0 && persistedNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <Bell size={32} className="mx-auto mb-2 opacity-30" />
                <p>Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {/* ⭐ NOVO: mensagens novas em tempo real, sempre no topo */}
                                {liveNotifications.map((notif, idx) => (
                  <a
                  
                    key={`live-${idx}`}
                    href={notif.conversationId ? `/conversation/${notif.conversationId}` : '/conversations'}
                    className="block p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xl flex-shrink-0">
                        <MessageCircle size={20} className="text-green-600" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {notif.message?.title || notif.title || 'Nova mensagem'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {notif.message?.message || ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notif.timestamp ? new Date(notif.timestamp).toLocaleString('pt-BR') : ''}
                        </p>
                      </div>
                    </div>
                  </a>
                ))}

                {/* ⭐ NOVO: pausas de campanha, opt-out, alerta de setor sem
                    resposta, etc. — persistidas, não somem se ninguém viu ao vivo */}
                {persistedNotifications.map((notif) => (
                  <div key={`persisted-${notif.id}`} className="p-3 hover:bg-gray-50">
                    <div className="flex items-start gap-2">
                      <span className="text-xl flex-shrink-0">🔔</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                        <p className="text-xs text-gray-500">{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleMarkPersistedAsRead(notif.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                        title="Marcar como lida"
                      >
                        <X size={14} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                ))}

                {notifications.map((notif) => (
                  <div key={notif.id} className="p-3 hover:bg-gray-50">
                    <div className="flex items-start gap-2">
                      <span className="text-xl flex-shrink-0">
                        {notif.alert_type === 'achievement_earned' ? '🏆' :
                         notif.alert_type === 'goal_not_met' ? '⚠️' :
                         notif.alert_type === 'slow_response' ? '⏱️' :
                         notif.alert_type === 'offline' ? '📴' :
                         notif.alert_type === 'long_conversation' ? '💬' : '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          {notif.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleMarkAsRead(notif.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                        title="Marcar como lida"
                      >
                        <X size={14} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
